import React, { useMemo, useState } from 'react'

import { Box, Flex, Image, Link, Text } from '@chakra-ui/react'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'

import ApplySkeleton from '@core/components/ApplyDialogV2/ApplySkeleton'
import ApplyV2Tooltip from '@core/components/ApplyDialogV2/ApplyV2Tooltip'
import TextEllipsis from '@core/components/TextEllipsis/TextEllipsis'
import decodeHtmlEntitiesRecursive from '@core/utils/helpers/decodeHtmlEntitiesRecursive'
import { getDateWithShortMonthName } from '@core/utils/helpers/getDateWithShortMonthName'
import { prettifyPrice } from '@core/utils/helpers/prettifyPrice'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'

import { Currencies } from '@page/logwork/helpers/currencies'
import PostPageV2GoogleMap from '@page/post-v2/components/PostPageV2GoogleMap'
import type { PostInfoV2 } from '@page/post-v2/types'

import facebookLogo from '../../assets/facebookLogo.svg'
import instagramLogo from '../../assets/instagramLogo.svg'
import linkedinLogo from '../../assets/linkedinLogo.svg'
import twitterLogo from '../../assets/twitterLogo.svg'
import './PostPageV2Info.scss'

const baseClass = 'PostPageV2Info'

interface PostPageV2InfoProps {
    postInfo?: PostInfoV2 | undefined | null
    isLoading?: boolean
}

function PostPageV2Info({ postInfo, isLoading }: PostPageV2InfoProps) {
    const { getTitle } = useApplyV2NavTranslations()
    const { t } = useTranslation()
    const [selectedLocation, setSelectedLocation] = useState<PostInfoV2['data']['locations'][number] | null>(null)
    const [selectedLocationIndex, setSelectedLocationIndex] = useState<number>(0)
    const [isLocationMapOpen, setIsLocationMapOpen] = useState(false)
    const locations = postInfo?.data?.locations ?? []

    const salaryCurrencyObject = useMemo(() => {
        if (postInfo?.data?.salary?.currencyId) {
            return Currencies.find((curr) => curr.id === postInfo?.data?.salary?.currencyId)
        }
        return null
    }, [postInfo])

    const getDifference = (expireDate: string) => {
        const date1 = dayjs(expireDate).startOf('day')
        const date2 = dayjs().startOf('day')

        const diffInMs = Math.abs(date1.diff(date2, 'millisecond'))
        const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))

        if (diffInDays === 0) {
            return `(${t('152')})`.toLowerCase()
        } else if (diffInDays === 1) {
            return `(${t('153')})`.toLowerCase()
        } else if (diffInDays < 0) {
            return ''
        } else {
            return `(${diffInDays} ${t('1039').toLowerCase()})`
        }
    }

    const getSalaryDara = () => {
        const { periodType, rangeType, amount, toAmount } = postInfo?.data?.salary || {}

        const getPeriodType = () => {
            switch (periodType) {
                case 1:
                    return getTitle('4912')
                case 2:
                    return getTitle('753')
                case 3:
                    return getTitle('754')
                case 4:
                    return getTitle('931')
                case 5:
                    return getTitle('1322')
                default:
                    return ''
            }
        }

        switch (rangeType) {
            case 1:
                return `${prettifyPrice(amount ?? null)} / ${getPeriodType()}`
            case 2:
                return `${prettifyPrice(amount ?? null)} - ${prettifyPrice(toAmount ?? null)} / ${getPeriodType()}`
            case 3:
            // return prettifyPrice(data.ad.PositionSalary) || t('Custom')
            // return prettifyPrice(data.ad.PositionSalary) || t('Custom')
            default:
                return getTitle('Not specified')
        }
    }

    function formatURL(url: string) {
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
            return `https://${url}`
        }
        return url
    }

    return (
        <div className={baseClass}>
            <div className={`${baseClass}__content`}>
                <ApplySkeleton isLoading={isLoading} width={'100%'} height={60} borderRadius="99999">
                    {postInfo?.data?.jobTitle && (
                        <PostPageV2InfoItem
                            type={'jobTitle'}
                            label={getTitle('322', 'Job title')}
                            content={decodeHtmlEntitiesRecursive(postInfo?.data?.jobTitle)}
                            icon={
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M4.06771 2.66771C4.06771 1.59996 4.93329 0.734375 6.00104 0.734375H10.001C11.0688 0.734375 11.9344 1.59996 11.9344 2.66771V3.78415H13.3344C14.4021 3.78415 15.2677 4.64973 15.2677 5.71748V13.3344C15.2677 14.4021 14.4021 15.2677 13.3344 15.2677H2.66771C1.59996 15.2677 0.734375 14.4021 0.734375 13.3344V5.71748C0.734375 4.64973 1.59996 3.78415 2.66771 3.78415H4.06771V2.66771ZM5.26771 3.78415H10.7344V2.66771C10.7344 2.2627 10.406 1.93437 10.001 1.93437H6.00104C5.59603 1.93437 5.26771 2.2627 5.26771 2.66771V3.78415ZM2.66771 4.98415C2.2627 4.98415 1.93437 5.31247 1.93437 5.71748V7.89757H14.0677V5.71748C14.0677 5.31247 13.7394 4.98415 13.3344 4.98415H2.66771ZM14.0677 9.09757H1.93437V13.3344C1.93437 13.7394 2.2627 14.0677 2.66771 14.0677H13.3344C13.7394 14.0677 14.0677 13.7394 14.0677 13.3344V9.09757Z"
                                        fill="#77777C"
                                    />
                                </svg>
                            }
                        />
                    )}
                </ApplySkeleton>
                <ApplySkeleton isLoading={isLoading} width={'100%'} height={60} borderRadius="99999">
                    {postInfo?.data?.company?.name && (
                        <PostPageV2InfoItem
                            type={'companyName'}
                            label={getTitle('713', 'Company')}
                            content={
                                <span className={`${baseClass}__item__desc__company`}>
                                    <span className={`${baseClass}__item__desc__company__name`}>
                                        {decodeHtmlEntitiesRecursive(postInfo?.data?.company?.name)}
                                    </span>
                                    <span className={`${baseClass}__item__desc__socials`}>
                                        {postInfo?.data?.company?.facebook && (
                                            <Box
                                                as="span"
                                                display="inline-flex"
                                                alignItems="center"
                                                _hover={{
                                                    transform: 'scale(1.2)',
                                                }}
                                            >
                                                <ApplyV2Tooltip
                                                    label="Facebook"
                                                    placement="top"
                                                    style={{
                                                        display: 'inline-flex',
                                                    }}
                                                >
                                                    <Link
                                                        href={formatURL(postInfo?.data?.company?.facebook)}
                                                        target={'_blank'}
                                                        aria-label={'Facebook'}
                                                        display={'inline'}
                                                        // opacity={isPostExpired ? 0.4 : 1}
                                                        _hover={{
                                                            transform: 'scale(1.2)',
                                                        }}
                                                        onClick={() => {
                                                            window.open(
                                                                formatURL(postInfo?.data?.company?.facebook),
                                                                '_blank'
                                                            )
                                                        }}
                                                    >
                                                        <Image
                                                            src={facebookLogo}
                                                            w={'20px'}
                                                            alt={'Facebook'}
                                                            // filter={isPostExpired ? 'saturate(0) opacity(0.7)' : 'none'}
                                                        />
                                                    </Link>
                                                </ApplyV2Tooltip>
                                            </Box>
                                        )}
                                        {postInfo?.data?.company?.instagram && (
                                            <Box
                                                as="span"
                                                display="inline-flex"
                                                alignItems="center"
                                                _hover={{
                                                    transform: 'scale(1.2)',
                                                }}
                                            >
                                                <ApplyV2Tooltip
                                                    label="Instagram"
                                                    placement="top"
                                                    style={{
                                                        display: 'inline-flex',
                                                    }}
                                                >
                                                    <Link
                                                        href={formatURL(postInfo?.data?.company?.instagram)}
                                                        target={'_blank'}
                                                        aria-label={'instagram'}
                                                        display={'inline'}
                                                        // opacity={isPostExpired ? 0.4 : 1}
                                                        _hover={{
                                                            transform: 'scale(1.2)',
                                                        }}
                                                        onClick={() =>
                                                            window.open(
                                                                formatURL(postInfo?.data?.company?.instagram),
                                                                '_blank'
                                                            )
                                                        }
                                                    >
                                                        <Image
                                                            src={instagramLogo}
                                                            w={'20px'}
                                                            alt={'Facebook'}
                                                            // filter={isPostExpired ? 'saturate(0) opacity(0.7)' : 'none'}
                                                        />
                                                    </Link>
                                                </ApplyV2Tooltip>
                                            </Box>
                                        )}

                                        {postInfo?.data?.company?.linkedin && (
                                            <Box
                                                as="span"
                                                display="inline-flex"
                                                alignItems="center"
                                                _hover={{
                                                    transform: 'scale(1.2)',
                                                }}
                                            >
                                                <ApplyV2Tooltip
                                                    label="Linkedin"
                                                    placement="top"
                                                    style={{
                                                        display: 'inline-flex',
                                                    }}
                                                >
                                                    <Link
                                                        href={formatURL(postInfo?.data?.company?.linkedin)}
                                                        target={'_blank'}
                                                        aria-label={'linkedin'}
                                                        display={'inline'}
                                                        // opacity={isPostExpired ? 0.4 : 1}
                                                        _hover={{
                                                            transform: 'scale(1.2)',
                                                        }}
                                                        onClick={() => {
                                                            window.open(
                                                                formatURL(postInfo?.data?.company?.linkedin),
                                                                '_blank'
                                                            )
                                                        }}
                                                    >
                                                        <Image
                                                            src={linkedinLogo}
                                                            w={'20px'}
                                                            alt={'LinkedIn'}
                                                            // filter={isPostExpired ? 'saturate(0) opacity(0.7)' : 'none'}
                                                        />
                                                    </Link>
                                                </ApplyV2Tooltip>
                                            </Box>
                                        )}
                                        {postInfo?.data?.company?.twitter && (
                                            <Box
                                                as="span"
                                                display="inline-flex"
                                                alignItems="center"
                                                _hover={{
                                                    transform: 'scale(1.2)',
                                                }}
                                            >
                                                <ApplyV2Tooltip
                                                    label="X"
                                                    placement="top"
                                                    style={{
                                                        display: 'inline-flex',
                                                    }}
                                                >
                                                    <Link
                                                        href={formatURL(postInfo?.data?.company?.twitter)}
                                                        target={'_blank'}
                                                        aria-label={'X'}
                                                        display={'inline'}
                                                        // opacity={isPostExpired ? 0.4 : 1}
                                                        _hover={{
                                                            transform: 'scale(1.2)',
                                                        }}
                                                        onClick={() => {
                                                            window.open(
                                                                formatURL(postInfo?.data?.company?.twitter),
                                                                '_blank'
                                                            )
                                                        }}
                                                    >
                                                        <Image
                                                            src={twitterLogo}
                                                            w={'20px'}
                                                            alt={'X'}
                                                            // filter={isPostExpired ? 'saturate(0) opacity(0.7)' : 'none'}
                                                        />
                                                    </Link>
                                                </ApplyV2Tooltip>
                                            </Box>
                                        )}
                                    </span>
                                </span>
                            }
                            icon={
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g clipPath="url(#clip0_11167_907)">
                                        <path
                                            d="M4.0651 1.60338C4.06522 0.733445 4.92234 0.122607 5.74479 0.40611L14.0781 3.27981C14.589 3.45598 14.9323 3.9367 14.9323 4.47707V14.0682H14.9987C15.3301 14.0682 15.599 14.3371 15.599 14.6685C15.5989 14.9998 15.33 15.2687 14.9987 15.2687H0.998698C0.667374 15.2687 0.398513 14.9998 0.398438 14.6685C0.398438 14.3371 0.667327 14.0682 0.998698 14.0682H1.0651V8.61119C1.06513 8.27532 1.19872 7.95287 1.4362 7.71535L4.0651 5.08645V1.60338ZM2.28516 8.56431C2.27272 8.57679 2.26565 8.59358 2.26562 8.61119V14.0682H4.0651V6.78437L2.28516 8.56431ZM5.35352 1.54023C5.31038 1.52558 5.26574 1.55779 5.26562 1.60338V14.0682H13.7318V4.47707C13.7318 4.44867 13.7137 4.4232 13.6868 4.41392L5.35352 1.54023ZM11.6654 10.7349C11.9967 10.7349 12.2656 11.0038 12.2656 11.3351C12.2655 11.6665 11.9967 11.9354 11.6654 11.9354H6.9987C6.66737 11.9354 6.39851 11.6665 6.39844 11.3351C6.39844 11.0038 6.66733 10.7349 6.9987 10.7349H11.6654ZM11.6654 8.06822C11.9967 8.06822 12.2656 8.33711 12.2656 8.66848C12.2655 8.99979 11.9967 9.26874 11.6654 9.26874H6.9987C6.66737 9.26874 6.39851 8.99979 6.39844 8.66848C6.39844 8.33711 6.66733 8.06822 6.9987 8.06822H11.6654ZM11.6654 5.40155C11.9967 5.40155 12.2656 5.67044 12.2656 6.00181C12.2655 6.33312 11.9967 6.60207 11.6654 6.60207H6.9987C6.66737 6.60207 6.39851 6.33312 6.39844 6.00181C6.39844 5.67044 6.66733 5.40155 6.9987 5.40155H11.6654Z"
                                            fill="#77777C"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_11167_907">
                                            <rect width="16" height="16" fill="white" />
                                        </clipPath>
                                    </defs>
                                </svg>
                            }
                        />
                    )}
                </ApplySkeleton>
                <ApplySkeleton isLoading={isLoading} width={'100%'} height={60} borderRadius="99999">
                    {postInfo?.data?.company?.workplace && (
                        <PostPageV2InfoItem
                            type={'workplace'}
                            label={getTitle('1109', 'Workplace')}
                            content={decodeHtmlEntitiesRecursive(postInfo?.data?.company?.workplace)}
                            icon={
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M14.8864 1.62119C15.0794 1.74046 15.1969 1.95113 15.1969 2.17798V11.6325C15.1969 11.8805 15.0568 12.1071 14.8351 12.218L10.4714 14.3998C10.2871 14.4919 10.0702 14.4919 9.88597 14.3998L5.81506 12.3643L1.74414 14.3998C1.54124 14.5012 1.30028 14.4904 1.10731 14.3711C0.914335 14.2519 0.796875 14.0412 0.796875 13.8143V4.3598C0.796875 4.11188 0.936949 3.88523 1.1587 3.77436L5.52234 1.59254C5.70661 1.5004 5.92351 1.5004 6.10778 1.59254L10.1787 3.628L14.2496 1.59254C14.4525 1.49109 14.6935 1.50193 14.8864 1.62119ZM9.52415 4.76433L6.4696 3.23706V11.228L9.52415 12.7553V4.76433ZM10.8332 12.7553L13.8878 11.228V3.23706L10.8332 4.76433V12.7553ZM5.16051 11.228V3.23706L2.10597 4.76433V12.7553L5.16051 11.228Z"
                                        fill="#77777C"
                                    />
                                </svg>
                            }
                        />
                    )}
                </ApplySkeleton>
                <ApplySkeleton isLoading={isLoading} width={'100%'} height={60} borderRadius="99999">
                    {postInfo?.data?.employmentType && (
                        <PostPageV2InfoItem
                            type={'employmentType'}
                            label={getTitle('8135', 'Employment type')}
                            content={decodeHtmlEntitiesRecursive(postInfo?.data?.employmentType)}
                            icon={
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M11.0013 6.06771C13.7259 6.06771 15.9349 8.2767 15.9349 11.0013C15.9349 13.7259 13.7259 15.9349 11.0013 15.9349C8.2767 15.9349 6.06771 13.7259 6.06771 11.0013C6.06771 8.2767 8.2767 6.06771 11.0013 6.06771ZM10.0013 0.734375C11.0691 0.734375 11.9349 1.60022 11.9349 2.66797V3.78451H13.3346C14.4023 3.78451 15.2681 4.64983 15.2682 5.71745V5.83138C15.2681 6.16265 14.9993 6.43099 14.668 6.43099C14.3367 6.43099 14.0678 6.16265 14.0677 5.83138V5.71745C14.0675 5.31257 13.7395 4.98437 13.3346 4.98437H2.66797C2.26306 4.98437 1.93506 5.31257 1.9349 5.71745V13.3346C1.9349 13.7396 2.26296 14.0677 2.66797 14.0677H5.33464C5.66601 14.0677 5.9349 14.3366 5.9349 14.668C5.9349 14.9993 5.66601 15.2682 5.33464 15.2682H2.66797C1.60022 15.2682 0.734375 14.4024 0.734375 13.3346V5.71745C0.734535 4.64983 1.60032 3.78451 2.66797 3.78451H4.06771V2.66797C4.06771 1.60022 4.93355 0.734375 6.0013 0.734375H10.0013ZM11.0013 7.26823C8.93944 7.26823 7.26823 8.93944 7.26823 11.0013C7.26823 13.0632 8.93944 14.7344 11.0013 14.7344C13.0632 14.7344 14.7344 13.0632 14.7344 11.0013C14.7344 8.93944 13.0632 7.26823 11.0013 7.26823ZM11.0013 10.3014C11.3327 10.3014 11.6016 10.5703 11.6016 10.9017V13.502C11.6012 13.8331 11.3325 14.1016 11.0013 14.1016C10.6701 14.1016 10.4014 13.8331 10.401 13.502V10.9017C10.401 10.5703 10.6699 10.3014 11.0013 10.3014ZM11.0013 8.33463C11.3695 8.33463 11.668 8.63311 11.668 9.0013C11.668 9.36949 11.3695 9.66797 11.0013 9.66797C10.6331 9.66797 10.3346 9.36949 10.3346 9.0013C10.3346 8.63311 10.6331 8.33463 11.0013 8.33463ZM6.0013 1.9349C5.59629 1.9349 5.26823 2.26296 5.26823 2.66797V3.78451H10.7344V2.66797C10.7344 2.26296 10.4063 1.9349 10.0013 1.9349H6.0013Z"
                                        fill="#77777C"
                                    />
                                </svg>
                            }
                        />
                    )}
                </ApplySkeleton>
                {(postInfo?.data?.employmentType ||
                    postInfo?.data?.workLocation ||
                    postInfo?.data?.sector ||
                    postInfo?.data?.jobType) && (
                    <div className={`${baseClass}__mobileHidden__details`}>
                        <div className={`${baseClass}__mobileHidden__details__icon`}>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                            >
                                <path
                                    d="M11.0013 6.06771C13.7259 6.06771 15.9349 8.2767 15.9349 11.0013C15.9349 13.7259 13.7259 15.9349 11.0013 15.9349C8.2767 15.9349 6.06771 13.7259 6.06771 11.0013C6.06771 8.2767 8.2767 6.06771 11.0013 6.06771ZM10.0013 0.734375C11.0691 0.734375 11.9349 1.60022 11.9349 2.66797V3.78451H13.3346C14.4023 3.78451 15.2681 4.64983 15.2682 5.71745V5.83138C15.2681 6.16265 14.9993 6.43099 14.668 6.43099C14.3367 6.43099 14.0678 6.16265 14.0677 5.83138V5.71745C14.0675 5.31257 13.7395 4.98437 13.3346 4.98437H2.66797C2.26306 4.98437 1.93506 5.31257 1.9349 5.71745V13.3346C1.9349 13.7396 2.26296 14.0677 2.66797 14.0677H5.33464C5.66601 14.0677 5.9349 14.3366 5.9349 14.668C5.9349 14.9993 5.66601 15.2682 5.33464 15.2682H2.66797C1.60022 15.2682 0.734375 14.4024 0.734375 13.3346V5.71745C0.734535 4.64983 1.60032 3.78451 2.66797 3.78451H4.06771V2.66797C4.06771 1.60022 4.93355 0.734375 6.0013 0.734375H10.0013ZM11.0013 7.26823C8.93944 7.26823 7.26823 8.93944 7.26823 11.0013C7.26823 13.0632 8.93944 14.7344 11.0013 14.7344C13.0632 14.7344 14.7344 13.0632 14.7344 11.0013C14.7344 8.93944 13.0632 7.26823 11.0013 7.26823ZM11.0013 10.3014C11.3327 10.3014 11.6016 10.5703 11.6016 10.9017V13.502C11.6012 13.8331 11.3325 14.1016 11.0013 14.1016C10.6701 14.1016 10.4014 13.8331 10.401 13.502V10.9017C10.401 10.5703 10.6699 10.3014 11.0013 10.3014ZM11.0013 8.33463C11.3695 8.33463 11.668 8.63311 11.668 9.0013C11.668 9.36949 11.3695 9.66797 11.0013 9.66797C10.6331 9.66797 10.3346 9.36949 10.3346 9.0013C10.3346 8.63311 10.6331 8.33463 11.0013 8.33463ZM6.0013 1.9349C5.59629 1.9349 5.26823 2.26296 5.26823 2.66797V3.78451H10.7344V2.66797C10.7344 2.26296 10.4063 1.9349 10.0013 1.9349H6.0013Z"
                                    fill="#77777C"
                                />
                            </svg>
                        </div>
                        <div className={`${baseClass}__mobileHidden__details__content`}>
                            {postInfo?.data?.employmentType && (
                                <div className={`${baseClass}__mobileHidden__details__content__item`}>
                                    {decodeHtmlEntitiesRecursive(postInfo?.data?.employmentType)}
                                </div>
                            )}
                            {postInfo?.data?.workLocation && (
                                <div className={`${baseClass}__mobileHidden__details__content__item`}>
                                    {decodeHtmlEntitiesRecursive(postInfo?.data?.workLocation)}
                                </div>
                            )}
                            {postInfo?.data?.sector && (
                                <div className={`${baseClass}__mobileHidden__details__content__item`}>
                                    {decodeHtmlEntitiesRecursive(postInfo?.data?.sector)}
                                </div>
                            )}
                            {postInfo?.data?.jobType && (
                                <div className={`${baseClass}__mobileHidden__details__content__item`}>
                                    {decodeHtmlEntitiesRecursive(postInfo?.data?.jobType)}
                                </div>
                            )}
                        </div>
                    </div>
                )}
                <ApplySkeleton isLoading={isLoading} width={'100%'} height={60} borderRadius="99999">
                    {postInfo?.data?.workLocation && (
                        <PostPageV2InfoItem
                            type={'workLocation'}
                            label={getTitle('357', 'Work location')}
                            content={decodeHtmlEntitiesRecursive(postInfo?.data?.workLocation)}
                        />
                    )}
                </ApplySkeleton>
                <ApplySkeleton isLoading={isLoading} width={'100%'} height={60} borderRadius="99999">
                    {postInfo?.data?.salary && (
                        <PostPageV2InfoItem
                            type={'salary'}
                            label={getTitle('329', 'Salary')}
                            content={
                                <Flex alignItems={'center'} gap={'4px'} className={`${baseClass}__item__salaryWrapper`}>
                                    {postInfo?.data?.salary?.rangeType !== 3 && (
                                        <>
                                            {salaryCurrencyObject?.img && (
                                                <Image
                                                    className={`${baseClass}__item__salaryImage__img`}
                                                    maxW={'14px'}
                                                    src={`${process.env.ENV === 'local' ? process.env.API_URL : window.location.origin}${salaryCurrencyObject.img}`}
                                                    alt={salaryCurrencyObject?.title || 'Currency'}
                                                    // filter={isPostExpired ? 'saturate(0) opacity(0.7)' : ''}
                                                />
                                            )}
                                            <Text
                                                fontSize={'sm'}
                                                fontWeight={'medium'}
                                                color={'text.main'}
                                                // opacity={isPostExpired ? 0.4 : 1}
                                            >
                                                {salaryCurrencyObject?.short}
                                            </Text>
                                        </>
                                    )}
                                    <Text
                                        fontSize={'sm'}
                                        fontWeight={'medium'}
                                        color={'text.main'}
                                        // opacity={isPostExpired ? 0.4 : 1}
                                    >
                                        {getSalaryDara()}
                                    </Text>
                                </Flex>
                            }
                            icon={
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M3.19844 2.77031C3.03931 2.77031 2.8867 2.83353 2.77417 2.94605C2.66165 3.05857 2.59844 3.21118 2.59844 3.37031V4.22739C3.02134 4.22737 3.42138 4.0671 3.71193 3.79038C4.00148 3.51462 4.15835 3.14704 4.15837 2.77031H3.19844ZM5.35837 2.77031C5.35835 3.48471 5.06009 4.16356 4.53952 4.65935C4.01994 5.15419 3.32122 5.42737 2.59844 5.42739V7.3133C3.32122 7.31332 4.01994 7.5865 4.53952 8.08134C5.06008 8.57711 5.35834 9.25593 5.35837 9.97031H10.6384C10.6384 9.25589 10.9366 8.57695 11.4572 8.08115C11.9768 7.58629 12.6756 7.3131 13.3984 7.3131L13.3984 5.42759C12.6756 5.42759 11.9768 5.1544 11.4572 4.65954C10.9366 4.16374 10.6384 3.48487 10.6384 2.77044L5.35837 2.77031ZM2.59844 9.37031V8.5133C3.02134 8.51332 3.42138 8.67359 3.71193 8.9503C4.00146 9.22605 4.15834 9.59361 4.15837 9.97031H3.19844C3.03931 9.97031 2.8867 9.9071 2.77417 9.79458C2.66165 9.68205 2.59844 9.52944 2.59844 9.37031ZM13.3984 3.37031V4.22759C12.9755 4.22759 12.5754 4.0673 12.2848 3.79058C11.9952 3.5148 11.8384 3.14719 11.8384 2.77044L12.7984 2.77031C12.9576 2.77031 13.1102 2.83353 13.2227 2.94605C13.3352 3.05857 13.3984 3.21118 13.3984 3.37031ZM13.3984 8.5131C12.9754 8.5131 12.5754 8.67339 12.2848 8.95011C11.9952 9.22589 11.8384 9.5935 11.8384 9.97025L12.7984 9.97031C12.9576 9.97031 13.1102 9.9071 13.2227 9.79458C13.3352 9.68205 13.3984 9.52944 13.3984 9.37031L13.3984 8.5131ZM1.92565 2.09752C2.26321 1.75995 2.72105 1.57031 3.19844 1.57031H12.7984C13.2758 1.57031 13.7337 1.75995 14.0712 2.09752C14.4088 2.43509 14.5984 2.89292 14.5984 3.37031V9.37031C14.5984 9.8477 14.4088 10.3055 14.0712 10.6431C13.7337 10.9807 13.2758 11.1703 12.7984 11.1703H3.19844C2.72105 11.1703 2.26321 10.9807 1.92565 10.6431C1.58808 10.3055 1.39844 9.8477 1.39844 9.37031V3.37031C1.39844 2.89292 1.58808 2.43509 1.92565 2.09752ZM7.99847 5.17038C7.33573 5.17038 6.79847 5.70764 6.79847 6.37038C6.79847 7.03312 7.33573 7.57038 7.99847 7.57038C8.66121 7.57038 9.19847 7.03312 9.19847 6.37038C9.19847 5.70764 8.66121 5.17038 7.99847 5.17038ZM5.59847 6.37038C5.59847 5.04489 6.67299 3.97038 7.99847 3.97038C9.32395 3.97038 10.3985 5.04489 10.3985 6.37038C10.3985 7.69586 9.32395 8.77038 7.99847 8.77038C6.67299 8.77038 5.59847 7.69586 5.59847 6.37038ZM13.9901 12.1694C14.0448 11.8426 13.8242 11.5333 13.4974 11.4786C13.1706 11.4239 12.8613 11.6445 12.8066 11.9714L12.6966 12.6291C12.6432 12.9478 12.3472 13.167 12.0268 13.125L2.67632 11.8999C2.34776 11.8569 2.04651 12.0883 2.00346 12.4169C1.96041 12.7454 2.19186 13.0467 2.52043 13.0897L11.8709 14.3148C12.832 14.4408 13.7201 13.7831 13.8801 12.8271L13.9901 12.1694Z"
                                        fill="#77777C"
                                    />
                                </svg>
                            }
                        />
                    )}
                </ApplySkeleton>
                {/*<ApplySkeleton isLoading={isLoading} width={'100%'} height={60} borderRadius="99999">*/}
                {postInfo?.data?.company?.webpage && !isLoading && (
                    <PostPageV2InfoItem
                        type={'webpage'}
                        label={getTitle('215', 'Webpage')}
                        content={
                            <Link
                                href={formatURL(postInfo?.data?.company?.webpage)}
                                color={'#245AF0'}
                                target={'_blank'}
                            >
                                <TextEllipsis>{postInfo?.data?.company?.webpage}</TextEllipsis>
                            </Link>
                        }
                        icon={
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M8.00104 1.93438C7.43026 1.93438 6.752 2.41268 6.18068 3.55532C5.997 3.92268 5.83467 4.34117 5.69976 4.80104H10.3023C10.1674 4.34117 10.0051 3.92268 9.82141 3.55532C9.25009 2.41268 8.57182 1.93438 8.00104 1.93438ZM5.10736 3.01867C4.84406 3.54528 4.62407 4.14606 4.45527 4.80104H2.846C3.45439 3.82303 4.33334 3.03083 5.37821 2.52909C5.28184 2.68662 5.1915 2.85039 5.10736 3.01867ZM4.21333 6.00104H2.27177C2.05319 6.62726 1.93438 7.30028 1.93438 8.00104C1.93438 8.56693 2.01185 9.11471 2.15678 9.63437H4.16398C4.1008 9.10799 4.06771 8.561 4.06771 8.00104C4.06771 7.30983 4.11813 6.63837 4.21333 6.00104ZM4.33168 10.6677H2.55036C3.15 11.8911 4.14936 12.8829 5.37821 13.473C5.28184 13.3155 5.1915 13.1517 5.10736 12.9834C4.77408 12.3168 4.51019 11.5314 4.33168 10.6677ZM5.56008 10.6677H10.442C10.286 11.3404 10.0738 11.9419 9.82141 12.4468C9.25009 13.5894 8.57182 14.0677 8.00104 14.0677C7.43026 14.0677 6.752 13.5894 6.18068 12.4468C5.92824 11.9419 5.71613 11.3404 5.56008 10.6677ZM10.6286 9.63437H5.37345C5.30478 9.11614 5.26771 8.56875 5.26771 8.00104C5.26771 7.29757 5.32464 6.62529 5.42805 6.00104H10.574C10.6774 6.62529 10.7344 7.29757 10.7344 8.00104C10.7344 8.56875 10.6973 9.11614 10.6286 9.63437ZM11.6704 10.6677C11.4919 11.5314 11.228 12.3168 10.8947 12.9834C10.8106 13.1517 10.7202 13.3155 10.6239 13.473C11.8527 12.8829 12.8521 11.8911 13.4517 10.6677H11.6704ZM13.8453 9.63437H11.8381C11.9013 9.10799 11.9344 8.561 11.9344 8.00104C11.9344 7.30983 11.8839 6.63837 11.7888 6.00104H13.7303C13.9489 6.62726 14.0677 7.30028 14.0677 8.00104C14.0677 8.56693 13.9902 9.11471 13.8453 9.63437ZM10.6239 2.52909C11.6687 3.03083 12.5477 3.82303 13.1561 4.80104H11.5468C11.378 4.14606 11.158 3.54528 10.8947 3.01867C10.8106 2.85039 10.7202 2.68661 10.6239 2.52909ZM8.00104 15.2677C12.0143 15.2677 15.2677 12.0143 15.2677 8.00104C15.2677 3.98777 12.0143 0.734375 8.00104 0.734375C3.98777 0.734375 0.734375 3.98777 0.734375 8.00104C0.734375 12.0143 3.98777 15.2677 8.00104 15.2677Z"
                                    fill="#77777C"
                                />
                            </svg>
                        }
                    />
                )}
                {/*</ApplySkeleton>*/}
                {/*<ApplySkeleton isLoading={isLoading} width={'100%'} height={60} borderRadius="99999">*/}
                {postInfo?.data?.sector && !isLoading && (
                    <PostPageV2InfoItem
                        type={'sector'}
                        label={getTitle('330', 'Sector')}
                        content={decodeHtmlEntitiesRecursive(postInfo?.data?.sector)}
                    />
                )}
                {/*</ApplySkeleton>*/}
                {/*<ApplySkeleton isLoading={isLoading} width={'100%'} height={60} borderRadius="99999">*/}
                {postInfo?.data?.jobType && !isLoading && (
                    <PostPageV2InfoItem
                        type={'jobType'}
                        label={getTitle('331', 'Job type')}
                        content={decodeHtmlEntitiesRecursive(postInfo?.data?.jobType)}
                    />
                )}
                {/*</ApplySkeleton>*/}
                {/*<ApplySkeleton isLoading={isLoading} width={'100%'} height={60} borderRadius="99999">*/}
                {postInfo?.data?.positions && !isLoading && (
                    <PostPageV2InfoItem
                        type={'positions'}
                        label={getTitle('2413', 'Positions')}
                        content={decodeHtmlEntitiesRecursive(postInfo?.data?.positions)}
                        icon={
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M11.1126 9.91406C12.2309 9.91409 13.3036 10.3583 14.0944 11.1491C14.8851 11.9399 15.3294 13.0126 15.3294 14.1309C15.3294 14.4622 15.0605 14.7305 14.7292 14.7305C14.3978 14.7305 14.129 14.4622 14.1289 14.1309C14.1289 13.3308 13.8112 12.5631 13.2454 11.9974C12.6798 11.4318 11.9126 11.114 11.1126 11.1139C10.3126 11.1139 9.5449 11.4317 8.97917 11.9974C8.41343 12.5631 8.0957 13.3308 8.0957 14.1309C8.09564 14.4621 7.82731 14.7303 7.49609 14.7305C7.16476 14.7305 6.8959 14.4622 6.89583 14.1309C6.89583 13.0125 7.34008 11.9399 8.13086 11.1491C8.92164 10.3583 9.9943 9.91406 11.1126 9.91406ZM4.88086 7.51823C5.62099 7.51825 6.34796 7.71328 6.98893 8.08333C7.27591 8.24902 7.37467 8.61602 7.20898 8.903C7.04332 9.18988 6.67626 9.28792 6.38932 9.1224C5.93076 8.85765 5.41036 8.71812 4.88086 8.7181C4.35132 8.7181 3.83099 8.85763 3.3724 9.1224C2.91381 9.38716 2.533 9.76798 2.26823 10.2266C2.00346 10.6852 1.86393 11.2055 1.86393 11.735C1.86387 12.0663 1.59554 12.3345 1.26432 12.3346C0.932991 12.3346 0.664125 12.0663 0.664063 11.735C0.664062 10.9948 0.859076 10.2673 1.22917 9.6263C1.5992 8.9855 2.13133 8.45336 2.77214 8.08333C3.41315 7.71324 4.14068 7.51823 4.88086 7.51823ZM11.2786 3.06901C12.9495 3.15384 14.278 4.53593 14.278 6.22786L14.2741 6.39062C14.1893 8.06139 12.8077 9.38984 11.1159 9.38997L10.9531 9.38607C9.33603 9.30422 8.03913 8.00766 7.95703 6.39062L7.95312 6.22786C7.95312 4.48126 9.36928 3.0651 11.1159 3.0651L11.2786 3.06901ZM11.1159 4.26497C10.032 4.26497 9.15299 5.144 9.15299 6.22786C9.15313 7.31161 10.0321 8.1901 11.1159 8.1901C12.1995 8.18997 13.078 7.31153 13.0781 6.22786C13.0781 5.14408 12.1996 4.26511 11.1159 4.26497ZM5.04427 0.667969C6.71512 0.7528 8.04362 2.13489 8.04362 3.82682L8.03971 3.98958C7.95488 5.66035 6.57333 6.9888 4.88151 6.98893L4.71875 6.98503C3.10165 6.90317 1.80475 5.60661 1.72266 3.98958L1.71875 3.82682C1.71875 2.08022 3.1349 0.664062 4.88151 0.664062L5.04427 0.667969ZM4.88151 1.86393C3.79765 1.86393 2.91862 2.74296 2.91862 3.82682C2.91876 4.91057 3.79773 5.78906 4.88151 5.78906C5.96518 5.78893 6.84361 4.91049 6.84375 3.82682C6.84375 2.74304 5.96526 1.86407 4.88151 1.86393Z"
                                    fill="#77777C"
                                />
                            </svg>
                        }
                    />
                )}
                {/*</ApplySkeleton>*/}
                {/*<ApplySkeleton isLoading={isLoading} width={'100%'} height={60} borderRadius="99999">*/}
                {postInfo?.data?.endDate && !isLoading && (
                    <PostPageV2InfoItem
                        type={'endDate'}
                        label={getTitle('326', 'End date')}
                        content={
                            <Text
                                fontSize={'sm'}
                                color={'text.main'}
                                fontWeight={'semibold'}
                                wordBreak={'break-word'}
                                // opacity={isPostExpired ? 0.4 : 1}
                            >
                                {getDateWithShortMonthName(postInfo?.data?.endDate, t)}{' '}
                                <Text as="span" color="#717175">
                                    {getDifference(postInfo?.data?.endDate)}
                                </Text>
                            </Text>
                        }
                        icon={
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M4.66771 0.734375C4.99908 0.734375 5.26771 1.003 5.26771 1.33438V2.63914H10.7344V1.33438C10.7344 1.003 11.003 0.734375 11.3344 0.734375C11.6657 0.734375 11.9344 1.003 11.9344 1.33438V2.63914H13.3344C14.4021 2.63914 15.2677 3.50472 15.2677 4.57247V13.3344C15.2677 14.4021 14.4021 15.2677 13.3344 15.2677H2.66771C1.59996 15.2677 0.734375 14.4021 0.734375 13.3344V4.57247C0.734375 3.50472 1.59996 2.63914 2.66771 2.63914H4.06771V1.33438C4.06771 1.003 4.33634 0.734375 4.66771 0.734375ZM2.66771 3.83914C2.2627 3.83914 1.93438 4.16746 1.93438 4.57247V5.91956H14.0677V4.57247C14.0677 4.16746 13.7394 3.83914 13.3344 3.83914H2.66771ZM14.0677 7.11956H1.93438V13.3344C1.93438 13.7394 2.2627 14.0677 2.66771 14.0677H13.3344C13.7394 14.0677 14.0677 13.7394 14.0677 13.3344V7.11956Z"
                                    fill="#77777C"
                                />
                            </svg>
                        }
                    />
                )}
                {/*</ApplySkeleton>*/}
                {/*<ApplySkeleton isLoading={isLoading} width={'100%'} height={60} borderRadius="99999">*/}
                {postInfo?.data?.applicationDeadline && !isLoading && (
                    <PostPageV2InfoItem
                        type={'applicationDeadline'}
                        label={getTitle('334', 'Application deadline')}
                        content={decodeHtmlEntitiesRecursive(postInfo?.data?.applicationDeadline)}
                        icon={
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M3.28434 4.12981C2.27639 3.12186 2.99026 1.39844 4.41571 1.39844H11.5873C13.0127 1.39844 13.7266 3.12186 12.7187 4.12981L8.85002 7.99844L12.7187 11.8671C13.7266 12.875 13.0127 14.5984 11.5873 14.5984H4.41571C2.99026 14.5984 2.27639 12.875 3.28434 11.8671L7.15297 7.99844L3.28434 4.12981ZM8.0015 7.14991L11.8701 3.28128C12.1221 3.02929 11.9436 2.59844 11.5873 2.59844H4.41571C4.05934 2.59844 3.88088 3.02929 4.13286 3.28128L8.0015 7.14991ZM8.0015 8.84697L4.13286 12.7156C3.88088 12.9676 4.05934 13.3984 4.41571 13.3984H11.5873C11.9436 13.3984 12.1221 12.9676 11.8701 12.7156L8.0015 8.84697Z"
                                    fill="#77777C"
                                />
                            </svg>
                        }
                    />
                )}
                {/*</ApplySkeleton>*/}
            </div>

            {locations.length > 0 && postInfo?.data?.showLocation && !isLoading && (
                <PostPageV2InfoItem
                    type={'locations'}
                    label={getTitle('5283', 'Locations')}
                    content={
                        <div className={`${baseClass}__content`}>
                            {locations.map((l, index) => (
                                <ApplyV2Tooltip key={l.placeId} label={l.formattedAddress} placement="top">
                                    <Box
                                        key={l.placeId}
                                        className={`${baseClass}__item__desc__location`}
                                        onClick={() => {
                                            setSelectedLocation(l)
                                            setSelectedLocationIndex(index)
                                            setIsLocationMapOpen(true)
                                        }}
                                        role={'button'}
                                        tabIndex={0}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault()
                                                setSelectedLocation(l)
                                                setSelectedLocationIndex(index)
                                                setIsLocationMapOpen(true)
                                            }
                                        }}
                                    >
                                        <div className={`${baseClass}__item__desc__location__icon`}>
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="12"
                                                height="15"
                                                viewBox="0 0 12 15"
                                                fill="none"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    clipRule="evenodd"
                                                    d="M2.46318 2.7584C4.3679 0.680533 7.6437 0.680533 9.54841 2.7584C11.2327 4.59576 11.2327 7.41584 9.54841 9.25319L6.0058 13.1179L2.46318 9.25319C0.778939 7.41584 0.778938 4.59576 2.46318 2.7584ZM10.433 1.94753C8.05268 -0.649177 3.95891 -0.649178 1.5786 1.94753C-0.5262 4.24368 -0.526199 7.76792 1.5786 10.0641L5.5635 14.4112C5.67715 14.5352 5.83761 14.6058 6.0058 14.6058C6.17398 14.6058 6.33444 14.5352 6.44809 14.4112L10.433 10.0641C12.5378 7.76792 12.5378 4.24368 10.433 1.94753ZM7.47246 6.0058C7.47246 6.81581 6.81581 7.47246 6.0058 7.47246C5.19578 7.47246 4.53913 6.81581 4.53913 6.0058C4.53913 5.19578 5.19578 4.53913 6.0058 4.53913C6.81581 4.53913 7.47246 5.19578 7.47246 6.0058ZM8.67246 6.0058C8.67246 7.47856 7.47856 8.67246 6.0058 8.67246C4.53304 8.67246 3.33913 7.47856 3.33913 6.0058C3.33913 4.53304 4.53304 3.33913 6.0058 3.33913C7.47856 3.33913 8.67246 4.53304 8.67246 6.0058Z"
                                                    fill="#245AF0"
                                                />
                                            </svg>
                                        </div>
                                        <div className={`${baseClass}__item__desc__location__address`}>
                                            <TextEllipsis>{l.formattedAddress}</TextEllipsis>
                                        </div>
                                    </Box>
                                </ApplyV2Tooltip>
                            ))}
                        </div>
                    }
                />
            )}

            {isLocationMapOpen && (
                <PostPageV2GoogleMap
                    location={selectedLocation}
                    showLocationNavigation={locations.length > 1}
                    onPreviousLocation={() => {
                        if (locations.length < 2) return
                        const previousIndex = (selectedLocationIndex - 1 + locations.length) % locations.length
                        setSelectedLocationIndex(previousIndex)
                        setSelectedLocation(locations[previousIndex])
                    }}
                    onNextLocation={() => {
                        if (locations.length < 2) return
                        const nextIndex = (selectedLocationIndex + 1) % locations.length
                        setSelectedLocationIndex(nextIndex)
                        setSelectedLocation(locations[nextIndex])
                    }}
                    onClose={() => {
                        setIsLocationMapOpen(false)
                        setSelectedLocation(null)
                    }}
                    onBackToJob={() => {
                        setIsLocationMapOpen(false)
                        setSelectedLocation(null)
                    }}
                />
            )}
        </div>
    )
}

interface PostPageV2InfoItemProps {
    label: string
    icon?: React.ReactNode
    content: React.ReactNode
    type:
        | 'jobTitle' // 1
        | 'companyName' // 0
        | 'workplace' // 3
        | 'employmentType' // 2
        | 'workLocation' // 5
        | 'salary' // 4
        | 'webpage' // 7
        | 'sector' //  6
        | 'jobType' // 9
        | 'positions' // 8
        | 'endDate' // 11
        | 'applicationDeadline' // 10
        | 'locations' // 12
}

function PostPageV2InfoItem({ label, icon, content, type }: PostPageV2InfoItemProps) {
    return (
        <div className={`${baseClass}__item ${baseClass}__item__${type}`}>
            <div className={`${baseClass}__item__label ${baseClass}__item__label__${type}`}>
                {icon && (
                    <div className={`${baseClass}__item__label__icon ${baseClass}__item__label__${type}__icon`}>
                        {icon}
                    </div>
                )}{' '}
                <div className={`${baseClass}__item__label__name ${baseClass}__item__label__${type}__name`}>
                    {label}
                </div>
            </div>
            <div className={`${baseClass}__item__desc ${baseClass}__item__${type}__desc`}>{content}</div>
        </div>
    )
}

export default PostPageV2Info
